/**
 * Baixar um anexo que está em OUTRO domínio.
 *
 * ## O defeito (Gisele, 23/10/2026)
 *
 * "Ao abrir uma imagem enviada pelo cliente, atualmente é possível somente
 * ampliar. Incluir a opção para baixar/download da imagem."
 *
 * O botão existia. O que não existia era o download: o anexo é servido por
 * `api-alodesk.sysfar.com.br` e a tela roda em `alodesk.sysfar.com.br`, e o
 * atributo `download` de um link é **ignorado quando a origem é outra** --
 * regra do navegador, não do nosso código. Com `target="_blank"` junto, o
 * clique abria a imagem numa aba nova. Para quem usa, "só amplia".
 *
 * O caminho que funciona entre origens é buscar o conteúdo e salvar um
 * endereço local (`blob:`), que é da própria página -- aí o `download` vale.
 *
 * ## O nome do arquivo também estava errado
 *
 * O nome saía do último pedaço da URL. Como a URL do anexo é
 * `/messages/{sala}/attachment?path=images/x.png`, o último pedaço é
 * `attachment?path=images%2Fx.png` -- um nome que o Windows recusa. O nome de
 * verdade está no parâmetro `path`.
 *
 * ## Por que há um plano B
 *
 * Se a busca falhar (rede, CORS, sessão), abrir o endereço ainda deixa a
 * pessoa salvar pelo menu do navegador. Pior que não baixar é o botão não
 * fazer nada.
 */

/**
 * O nome que o arquivo deve ter ao ser salvo.
 *
 * @param {string} url
 * @param {string} [padrao]
 */
export const nomeDoArquivo = (url, padrao = 'arquivo') => {
    const texto = String(url || '').trim();

    if (texto === '') return padrao;

    // `data:` e `blob:` não carregam nome nenhum.
    if (/^(data:|blob:)/i.test(texto)) return padrao;

    const [semHash] = texto.split('#');
    const [caminho, consulta = ''] = semHash.split('?');

    // O `path` do anexo de chat é quem sabe o nome real.
    const doParametro = new URLSearchParams(consulta).get('path');
    const base = doParametro || caminho;

    const ultimo = String(base).split('/').filter(Boolean).pop();

    if (!ultimo) return padrao;

    try {
        return decodeURIComponent(ultimo) || padrao;
    } catch (e) {
        // Percentual solto no nome (`%` sem par) estoura o decode. O nome cru
        // ainda serve -- melhor que cair na linha de baixo sem baixar nada.
        return ultimo;
    }
};

/** Dispara o salvamento de um endereço já local. */
const salvarEndereco = (endereco, nome) => {
    const link = document.createElement('a');

    link.href = endereco;
    link.download = nome;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

/**
 * Baixa o arquivo, venha ele de onde vier.
 *
 * @param {string|Blob|File} arquivo
 * @param {object} [deps] para o teste: `fetch`, `criarEndereco`, `revogar`
 * @returns {Promise<'blob'|'direto'>} como foi baixado
 */
export const baixarArquivo = async (arquivo, deps = {}) => {
    const {
        buscar = (...args) => fetch(...args),
        criarEndereco = (b) => URL.createObjectURL(b),
        revogar = (e) => URL.revokeObjectURL(e),
    } = deps;

    // Blob ou File em mãos: nada a buscar.
    if (arquivo && typeof arquivo !== 'string') {
        const endereco = criarEndereco(arquivo);
        salvarEndereco(endereco, arquivo.name || 'arquivo');
        revogar(endereco);
        return 'blob';
    }

    const url = String(arquivo || '').trim();

    if (url === '') return 'direto';

    const nome = nomeDoArquivo(url);

    // `data:` e `blob:` já são locais: buscar seria dar uma volta à toa.
    if (/^(data:|blob:)/i.test(url)) {
        salvarEndereco(url, nome);
        return 'direto';
    }

    try {
        const resposta = await buscar(url);

        if (!resposta || !resposta.ok) throw new Error('resposta não ok');

        const conteudo = await resposta.blob();
        const endereco = criarEndereco(conteudo);

        salvarEndereco(endereco, nome);
        revogar(endereco);

        return 'blob';
    } catch (e) {
        // Plano B: abrir. Não baixa sozinho, mas a pessoa salva pelo menu --
        // e é exatamente o que o botão fazia antes, então nada piora.
        salvarEndereco(url, nome);
        return 'direto';
    }
};
